import {
  closeCurrentDialog,
  Form,
  FormControl,
  FormField,
  showNotification,
} from 'platform/components';
import {Show, VStack} from 'platform/foundation';
import {z} from 'zod';

import {useWatch} from 'react-hook-form';

import {defaultTo} from 'ramda';

import {
  CoreExchangeItemsTab,
  usePatchCoreExchangeGeneralSettingsMutation,
} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {
  getOptionsFromCustomersAsSupplier,
  handleApiError,
  useGetSupplierCustomersQuery,
} from '@dms/shared';

import {Nullish, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {DialogActions} from './DialogActions';

const GeneralSettingsFormSchema = z
  .object({
    generalNote: z.string(),
    returnCoreItemToSupplier: z.boolean(),
    supplierId: z.string(),
  })
  .refine((values) => !values.returnCoreItemToSupplier || values.supplierId.length > 0, {
    path: ['supplierId'],
    message: i18n.t('errors.field_is_required'),
  });

type GeneralSettingsForm = z.infer<typeof GeneralSettingsFormSchema>;

interface GeneralSettingsDialogProps extends RequiredTestIdProps {
  coreExchangeId: string;
  general: CoreExchangeItemsTab['general'] | Nullish;
  onSaved: VoidFunction;
}

export function GeneralSettingsDialog(props: GeneralSettingsDialogProps) {
  const [patchGeneralSettings, {isLoading}] = usePatchCoreExchangeGeneralSettingsMutation();
  const {data: suppliers, isLoading: areSuppliersLoading} = useGetSupplierCustomersQuery();

  const handleSubmit = async (values: GeneralSettingsForm) => {
    await patchGeneralSettings({coreExchangeId: props.coreExchangeId, body: values})
      .unwrap()
      .then(() => {
        showNotification.success(i18n.t('general.notifications.successfullyUpdated'));
        props.onSaved();
        closeCurrentDialog();
      })
      .catch(handleApiError);
  };

  return (
    <Form<GeneralSettingsForm>
      experimentalZodSchema={GeneralSettingsFormSchema}
      onSubmit={handleSubmit}
      defaultValues={{
        generalNote: defaultTo('', props.general?.generalNote),
        returnCoreItemToSupplier: defaultTo(false, props.general?.returnCoreItemToSupplier),
        supplierId: defaultTo('', props.general?.supplierId),
      }}
    >
      {(control) => (
        <VStack spacing={4}>
          <FormField
            control={control}
            type="textarea"
            name="generalNote"
            label={i18n.t('entity.condition.labels.generalNote')}
            data-testid={suffixTestId('inputs.generalNote', props)}
          />
          <ReturnToSupplierFields
            control={control}
            supplierOptions={getOptionsFromCustomersAsSupplier(defaultTo([], suppliers))}
            areSuppliersLoading={areSuppliersLoading}
            data-testid={props['data-testid']}
          />
          <DialogActions
            control={control}
            isLoading={isLoading}
            data-testid={props['data-testid']}
          />
        </VStack>
      )}
    </Form>
  );
}

interface ReturnToSupplierFieldsProps extends RequiredTestIdProps {
  control: FormControl<GeneralSettingsForm>;
  supplierOptions: ReturnType<typeof getOptionsFromCustomersAsSupplier>;
  areSuppliersLoading: boolean;
}

function ReturnToSupplierFields(props: ReturnToSupplierFieldsProps) {
  const returnCoreItemToSupplier = useWatch({
    control: props.control,
    name: 'returnCoreItemToSupplier',
  });

  return (
    <>
      <FormField
        control={props.control}
        type="switch"
        name="returnCoreItemToSupplier"
        label={i18n.t('entity.warehouse.labels.coreExchangeReturnCoreItemToSupplier')}
        helperText={
          returnCoreItemToSupplier
            ? undefined
            : i18n.t('entity.warehouse.labels.coreExchangeCoreWillNotBeReturned')
        }
        data-testid={suffixTestId('inputs.returnCoreItemToSupplier', props)}
      />
      <Show when={returnCoreItemToSupplier}>
        <FormField
          control={props.control}
          type="choice"
          name="supplierId"
          label={i18n.t('entity.warehouse.labels.supplier')}
          options={props.supplierOptions}
          isLoading={props.areSuppliersLoading}
          isNotClearable
          isRequired
          menuInPortal
          data-testid={suffixTestId('inputs.supplierId', props)}
        />
      </Show>
    </>
  );
}
